<script setup lang="ts">
    import Highcharts from 'highcharts';
    import type { Options, SeriesOptionsType } from 'highcharts';
    import 'highcharts/modules/drilldown';
    import { Chart } from 'highcharts-vue';
    import { ref, computed } from 'vue';
    import { ILineChart } from '@/js/services/api/widget/types';
    import { __ } from '../../../stores/expressions';
    import BaseChart from './BaseChart.vue';
    import { getZIndex } from '@/js/utils/zIndex';

    const props = defineProps<{
        data: ILineChart['lineChart'];
    }>();

    const chartColors: string[] = [
        'var(--color-primary-beet)',
        'var(--color-primary-blueberry)',
        'var(--color-primary-carrot)',
        'var(--color-primary-avocado-400)',
        'var(--color-primary-lime)',
        'var(--color-primary-plum)',
        'var(--color-primary-raspberry)',
        'var(--color-primary-blueberry-400)',
        'var(--color-primary-carrot-400)',
        'var(--color-primary-acai)',
        'var(--color-primary-avocado)',
        'var(--color-primary-beet-400)',
    ];

    const type: string = 'spline';
    const rootLevelName: string = __('chart-main-serie');
    const unitType: string = props.data.unitType == '%' ? __('percentage-character') : '';
    const maxChars = 25;

    const dataWithUnixTimestamps = props.data.data.map((item) => ({
        ...item,
        unixTimestamp: new Date(item.timestamp).getTime(),
    }));

    const uniqueTimestamps = Array.from(
        new Set(dataWithUnixTimestamps.map((item) => item.unixTimestamp))
    );

    const idToSortOrder = new Map(dataWithUnixTimestamps.map((i) => [i.id, i.sortOrder]));

    const distinctIds = [...new Set(dataWithUnixTimestamps.map((item) => item.id))].sort((a, b) => {
        const orderA = idToSortOrder.get(a) ?? null;
        const orderB = idToSortOrder.get(b) ?? null;
        if (orderA === null && orderB === null) return 0;
        if (orderA === null) return 1;
        if (orderB === null) return -1;
        return orderA - orderB;
    });

    const legendEnabled = distinctIds.length > 1;

    const allSeries = distinctIds.map((id, index) => {
        const dataPoints = uniqueTimestamps.map((unixTimestamp) => {
            const matching = dataWithUnixTimestamps.find(
                (i) => i.id === id && i.unixTimestamp === unixTimestamp
            );

            return {
                x: unixTimestamp,
                y: matching
                    ? props.data.unitType === '%'
                        ? parseFloat(matching.value.toFixed(1))
                        : matching.value
                    : null,
                name: matching ? matching.name : String(unixTimestamp),
                drilldown:
                    dataWithUnixTimestamps.filter((item) => item.parentId === id).length > 0
                        ? id
                        : null,
            };
        });

        let name =
            dataWithUnixTimestamps.find((i) => i.id === id)?.name ||
            `${__('chart-fallback-serie-name', { id })}`;

        const fullName = name;

        if (!legendEnabled) {
            name = '';
        }

        const displayName = name.length > maxChars ? name.substring(0, maxChars) + '…' : name;

        return {
            id,
            name: displayName,
            type: 'spline',
            color: chartColors[index % chartColors.length],
            data: dataPoints,
            custom: {
                isRootLevel: !dataWithUnixTimestamps.find((i) => i.id === id)?.parentId,
                fullName,
            },
        } as SeriesOptionsType;
    });

    // --- Store breadcrumb path as an array of names ---
    var breadcrumbPath = ref<string[]>([]);
    var chart: Highcharts.Chart | null = null;

    // Helper: Get root series name (safety check)
    const rootSeries = allSeries.filter((item) => item.custom?.isRootLevel);
    const baseOptions = computed<Options>(() => ({
        chart: {
            type: 'line',
            style: {
                fontFamily: 'inherit',
            },
            marginTop: 10,
            events: {
                drilldown: function (e: any) {
                    if (!e.point.drilldown) return;

                    chart = this;

                    // Find child series based on parentId
                    const newSeries = allSeries.filter((series) =>
                        dataWithUnixTimestamps.some(
                            (item) => item.id === series.id && item.parentId === e.point.drilldown
                        )
                    );

                    // Add current level to breadcrumbs (clicked point name)
                    breadcrumbPath.value.push(e.point.name);

                    while (chart.series.length > 0) {
                        chart.series[0].remove(false);
                    }

                    newSeries.forEach((serie) => {
                        chart?.addSeries(serie, false);
                    });

                    chart.update(
                        {
                            chart: {
                                marginTop: 40,
                            },
                        },
                        false
                    );

                    chart.redraw();
                },
            },
        },
        drilldown: {
            activeAxisLabelStyle: {
                textDecoration: 'none',
                fontWeight: 'normal',
                color: 'var(--color-gray-dark)',
                pointerEvents: 'none',
            },
        },
        title: { text: '' },
        tooltip: {
            enabled: true,
            outside: true,
            useHTML: true,
            shadow: false,
            backgroundColor: '#ffffff',
            borderColor: 'var(--chart-background-color)',
            borderWidth: 1,
            borderRadius: 6,
            style: {
                fontFamily: 'inherit',
                zIndex: getZIndex('tooltip'),
            },
            formatter: function (this: any) {
                const fullName =
                    this.point.series.options.custom?.fullName || this.point.series.name;
                return `<div style="width:max-content;max-width:250px;white-space:normal;word-break:break-word;">${fullName}<br/>${this.point.y}\u00A0${unitType}</div>`;
            },
        },
        legend: {
            enabled: legendEnabled,
            align: 'right',
            verticalAlign: 'top',
            layout: 'vertical',
            floating: false,
            maxWidth: 150,
            navigation: {
                activeColor: 'var(--chart-background-color)',
                animation: true,
                arrowSize: 10,
                inactiveColor: 'var(--chart-background-color)',
            },
            maxHeight: 250,
            itemStyle: {
                fontSize: '12px',
            },
        },
        exporting: {
            enabled: false,
        },
        credits: { enabled: false },
        accessibility: {
            enabled: true,
            keyboardNavigation: {
                enabled: true,
            },
        },
        plotOptions: {
            series: {
                lineWidth: type == 'spline' ? 2 : 0,
                marker: {
                    enabled: type == 'spline',
                },
                connectNulls: true,
                dataLabels: {
                    enabled: false,
                },
                states: {
                    hover: {
                        enabled: true,
                    },
                    inactive: {
                        enabled: false,
                    },
                },
            },
        },
        xAxis: {
            type: 'datetime',
            lineColor: 'var(--chart-background-color)',
            lineWidth: 2,
            labels: {
                format: '{value:%Y-%m-%d}',
                style: {
                    fontSize: '12px',
                },
            },
        },
        yAxis: {
            title: {
                text: undefined,
            },
            gridLineDashStyle: 'Dash',
            lineColor: 'var(--chart-background-color)',
            lineWidth: 2,
            labels: {
                style: {
                    fontSize: '12px',
                },
            },
        },
        series: rootSeries,
    }));

    // --- Function to handle breadcrumb clicks ---
    function onBreadcrumbClick() {
        // Update breadcrumbPath to the correct level
        breadcrumbPath.value.pop();
        let newSeries;
        newSeries = rootSeries;

        // Remove all series from the chart
        if (chart) {
            while (chart.series.length > 0) {
                chart.series[0].remove(false);
            }

            // Add new series
            newSeries.forEach((serie) => {
                chart?.addSeries(serie, false);
            });

            // Adjust margin and redraw
            chart.update(
                {
                    chart: {
                        marginTop: 10,
                    },
                },
                false
            );

            chart.redraw();
        }
    }
</script>

<template>
    <div v-if="breadcrumbPath.length" class="absolute top-5 left-12 z-10 text-[0.8em]">
        <a
            class="px-0.5 py-px mx-1.5 text-black cursor-pointer hover:bg-gray-300/45"
            @click="onBreadcrumbClick()"
        >
            {{ rootLevelName }}
        </a>
        <span>/</span>
        <span class="px-0.5 py-px mx-1.5 text-black cursor-pointer hover:bg-gray-300/45 font-bold">
            {{ breadcrumbPath[0] }}
        </span>
    </div>
    <div class="Chart__container" style="--chart-background-color: var(--color-gray-lightest)">
        <BaseChart :has-data="allSeries.length > 0">
            <Chart :options="baseOptions" :highcharts="Highcharts" />
        </BaseChart>
    </div>
</template>
